import { useEffect, useMemo, useState } from "react";
import * as XLSX from 'xlsx';
import dayjs from "dayjs";
import { DataGrid } from '@mui/x-data-grid';
import PageHeader from "../../../components/PageHeader";
import { Autocomplete, Box, Button, Card, Checkbox, createTheme, Link, TextField, ThemeProvider, useTheme } from "@mui/material";
import "./Practices.css";
import { useSelector } from "react-redux";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { selectChildPractices, selectCustomerError, selectGroups, selectLoadingState } from "../../../redux/customer";
import { useAppDispatch } from "../../../redux/store";
import { addError } from '../../../redux/alerts';
import { Groups, LoadingState } from "../../../types";
import { useNavigate } from "react-router-dom";

const tableTheme = createTheme({
  typography: {
    fontFamily: '"Open Sans", sans-serif',
    fontSize: 13
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: "bold"
        }
      }
    }
  }
});

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const Practices = () => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const groups = useSelector(selectGroups);
  const childPractices = useSelector(selectChildPractices);
  const errorMessage = useSelector(selectCustomerError);
  const loadingState = useSelector(selectLoadingState)

  const [groupFilter, setGroupFilter] = useState<Groups[]>([]);
  const [search, setSearch] = useState<string>('');
  
  const isLoading = loadingState === LoadingState.Loading

  useEffect(() => {
    if (errorMessage !== null) {
      dispatch(addError(errorMessage));
    }
  }, [errorMessage])

  const filteredPractices = useMemo(() => {
    let practices = structuredClone(childPractices) ?? [];
    if (search.length > 0) {
      const cleanSearch = search.toLowerCase();
      practices = practices.filter(({ customer_number, name, doctor_name, street, phone, city, zip, state }) => {
        return customer_number.includes(cleanSearch)
          || name.toLowerCase().includes(cleanSearch)
          || doctor_name.toLowerCase().includes(cleanSearch)
          || street.toLowerCase().includes(cleanSearch)
          || phone.toLowerCase().includes(cleanSearch)
          || city.toLowerCase().includes(cleanSearch)
          || zip.toLowerCase().includes(cleanSearch)
          || state.toLowerCase().includes(cleanSearch)
      })
    }

    if (groupFilter.length > 0) {
      const members = groupFilter.flatMap((group) => group?.members.map((member) => member.customer_number));
      if (members) {
        practices = practices.filter((customer) => members.includes(customer.customer_number));
      }
    }

    return practices;
  }, [childPractices, groupFilter, search])

  const exportPractices = () => {
    const formattedChildPractices = filteredPractices
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(({ customer_number, name, doctor_name, street, city, state, zip, phone, last_connection_date }) => (
        {
          'Customer Number': customer_number,
          'Customer Name': name,
          'Doctor Name': doctor_name,
          Street: street,
          City: city,
          State: state,
          Zip: zip,
          Phone: phone,
          'Last Login': last_connection_date ? dayjs(last_connection_date).format('MM/DD/YYYY') : ''
        }
      ));

    const ws = XLSX.utils.json_to_sheet(formattedChildPractices);
    const wb = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(wb, ws, 'Practices');
    XLSX.writeFile(wb, 'Practice_List.xlsx');
  }

  return (
    <>
      <PageHeader iconName={'building'} headerName={'Practices'} />
      <Box sx={{ width: '100%', p: 2, color: theme.palette.color50555b.main }} className="training-container">
        <Card sx={{ width: "100%" }} variant="outlined">
          <div className="box-header flex flex-space-between flex-align-center">{filteredPractices.length} Practices</div>
          <div className="table-subheader flex-space-between">
            <div className="flex">
              <TextField sx={{ mr: 2, minWidth: 200 }} size="small" label="Search" onChange={(event) => setSearch(event.target.value)}></TextField>
              {groups.length > 0 && (
                <Autocomplete
                  multiple
                  onChange={(event, newValue) => {
                    setGroupFilter(newValue);
                  }}
                  options={groups}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, option, { selected }) => (
                    <li {...props}>
                      <Checkbox icon={icon} checkedIcon={checkedIcon} style={{ marginRight: 8 }} checked={selected} />
                      {option.name}
                    </li>
                  )}
                  size="small"
                  style={{ minWidth: 200, maxWidth: 500, marginRight: '16px' }}
                  renderInput={(params) => <TextField {...params} label="Filter by Group" />}
                />
              )}
            </div>
            <Button variant="contained" onClick={() => exportPractices()}>Export</Button>
          </div>
          <ThemeProvider theme={tableTheme}>
            <DataGrid
              sx={{ borderLeft: "none", borderRight: "none", borderBottom: "none", borderRadius: "0" }}
              rows={filteredPractices.map(practice => ({ ...practice, id: practice.customer_number }))}
              columns={[
                {
                  field: "customer_number",
                  headerName: "Customer #",
                  sortable: true,
                  width: 100,
                  renderCell: (params) => (
                    <Link sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }} component="button" onClick={() => navigate(`/corp/practice/${params.row.customer_number}/dashboard`)}>
                      {params.row.customer_number.trim()}
                    </Link>
                  )
                },
                {
                  field: "name",
                  headerName: "Name",
                  width: 300,
                  renderCell: (params) => (
                    <Link sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }} component="button" onClick={() => navigate(`/corp/practice/${params.row.customer_number}/dashboard`)}>
                      {params.row.name.trim()}
                    </Link>
                  )
                },
                { field: "doctor_name", headerName: "Doctor Name", width: 250 },
                { field: "street", headerName: "Street", width: 250 },
                { field: "city", headerName: "City", width: 150 },
                { field: "state", headerName: "State", width: 100 },
                { field: "zip", headerName: "Zip", width: 100 },
                { field: "phone", headerName: "Phone", width: 150 },
                {
                  field: "last_connection_date",
                  headerName: "Last Login",
                  width: 150,
                  renderCell: (params) => (
                    params.row.last_connection_date ? dayjs(params.row.last_connection_date).format('MM/DD/YYYY')
                      : <Link component="button" sx={{ color: theme.palette.linkColor.main, textDecoration: 'none' }} onClick={() => navigate(`/corp/practice/${params.row.customer_number}/team-members`)}>
                        Set-up Required
                      </Link>
                  )
                }
              ]}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 10,
                  },
                },
                sorting: {
                  sortModel: [
                    {
                      field: 'name',
                      sort: 'asc'
                    }
                  ]
                }
              }}
              pageSizeOptions={[5, 10, 20, 25, 50, 100]}
              loading={isLoading}
              autoHeight
              density="compact"
            />
          </ThemeProvider>
        </Card>
      </Box>
    </>
  );
}

export default Practices;